<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  getAccountBranding,
  updateAccountBranding,
} from 'dashboard/api/accountBranding';

const route = useRoute();
const { t } = useI18n();
const accountId = computed(() => Number(route.params.accountId));
const loading = ref(true);
const saving = ref(false);
const primaryColor = ref('#9f40df');
const logo = ref(null);
const favicon = ref(null);
const logoInput = ref(null);
const faviconInput = ref(null);
const removeLogo = ref(false);
const removeFavicon = ref(false);
const current = ref({});

const load = async () => {
  loading.value = true;
  try {
    const { data } = await getAccountBranding(accountId.value);
    current.value = data;
    primaryColor.value = data.primary_color || '#9f40df';
  } catch {
    useAlert(t('ACCOUNT_BRANDING.ALERTS.LOAD_FAILED'));
  } finally {
    loading.value = false;
  }
};

const chooseFile = (event, target) => {
  const file = event.target.files?.[0] || null;
  if (target === 'logo') {
    logo.value = file;
    removeLogo.value = false;
  } else {
    favicon.value = file;
    removeFavicon.value = false;
  }
  event.target.value = '';
};

const openFilePicker = target => {
  const input = target === 'logo' ? logoInput.value : faviconInput.value;
  input?.click();
};

const save = async () => {
  saving.value = true;
  try {
    const payload = new FormData();
    payload.append('primary_color', primaryColor.value);
    if (logo.value) payload.append('logo', logo.value);
    if (favicon.value) payload.append('favicon', favicon.value);
    if (removeLogo.value) payload.append('remove_logo', 'true');
    if (removeFavicon.value) payload.append('remove_favicon', 'true');
    const { data } = await updateAccountBranding(accountId.value, payload);
    current.value = data;
    logo.value = null;
    favicon.value = null;
    removeLogo.value = false;
    removeFavicon.value = false;
    window.dispatchEvent(new CustomEvent('taco-account-branding-updated', {
      detail: { accountId: accountId.value, branding: data },
    }));
    useAlert(t('ACCOUNT_BRANDING.ALERTS.SAVED'));
  } catch {
    useAlert(t('ACCOUNT_BRANDING.ALERTS.SAVE_FAILED'));
  } finally {
    saving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="t('ACCOUNT_BRANDING.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('ACCOUNT_BRANDING.TITLE')"
        :description="t('ACCOUNT_BRANDING.DESCRIPTION')"
      />
    </template>
    <form
      class="mx-auto flex w-full max-w-3xl flex-col gap-6 px-6 py-5 text-start"
      @submit.prevent="save"
    >
      <section class="grid gap-5 rounded-lg border border-n-weak bg-n-solid-2 p-5 md:grid-cols-2">
        <label class="flex flex-col gap-2">
          <span class="font-medium text-n-slate-12">
            {{ t('ACCOUNT_BRANDING.PRIMARY_COLOR.LABEL') }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{ t('ACCOUNT_BRANDING.PRIMARY_COLOR.DESCRIPTION') }}
          </span>
          <div class="flex items-center gap-3" dir="ltr">
            <input v-model="primaryColor" type="color" class="size-10 rounded-md border border-n-strong p-1" />
            <input v-model="primaryColor" pattern="#[0-9A-Fa-f]{6}" class="h-10 flex-1 rounded-lg border border-n-strong px-3" />
          </div>
        </label>
        <div class="flex items-center justify-center rounded-md bg-n-alpha-2 p-5">
          <button
            type="button"
            class="rounded-lg px-5 py-2 font-medium text-white"
            :style="{ backgroundColor: primaryColor }"
          >
            {{ t('ACCOUNT_BRANDING.PRIMARY_COLOR.PREVIEW') }}
          </button>
        </div>
      </section>
      <section class="grid gap-5 rounded-lg border border-n-weak bg-n-solid-2 p-5 md:grid-cols-2">
        <label class="flex flex-col gap-2">
          <span class="font-medium text-n-slate-12">
            {{ t('ACCOUNT_BRANDING.LOGO.LABEL') }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{ t('ACCOUNT_BRANDING.LOGO.DESCRIPTION') }}
          </span>
          <input
            ref="logoInput"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            class="sr-only"
            @change="chooseFile($event, 'logo')"
          />
          <div class="flex min-w-0 items-center gap-3">
            <Button
              type="button"
              icon="i-lucide-upload"
              variant="outline"
              size="sm"
              :label="t('ACCOUNT_BRANDING.ACTIONS.CHOOSE_FILE')"
              @click="openFilePicker('logo')"
            />
            <span class="min-w-0 truncate text-sm text-n-slate-11">
              {{ logo?.name || t('ACCOUNT_BRANDING.ACTIONS.NO_FILE_SELECTED') }}
            </span>
          </div>
          <div v-if="current.logo_url && !removeLogo" class="mt-2 flex items-center gap-3">
            <img
              :src="current.logo_url"
              :alt="t('ACCOUNT_BRANDING.LOGO.CURRENT_ALT')"
              class="max-h-16 max-w-48 object-contain"
            />
            <Button
              icon="i-lucide-trash-2"
              variant="ghost"
              color="ruby"
              size="sm"
              :label="t('ACCOUNT_BRANDING.ACTIONS.REMOVE')"
              @click="removeLogo = true"
            />
          </div>
        </label>
        <label class="flex flex-col gap-2">
          <span class="font-medium text-n-slate-12">
            {{ t('ACCOUNT_BRANDING.FAVICON.LABEL') }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{ t('ACCOUNT_BRANDING.FAVICON.DESCRIPTION') }}
          </span>
          <input
            ref="faviconInput"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            class="sr-only"
            @change="chooseFile($event, 'favicon')"
          />
          <div class="flex min-w-0 items-center gap-3">
            <Button
              type="button"
              icon="i-lucide-upload"
              variant="outline"
              size="sm"
              :label="t('ACCOUNT_BRANDING.ACTIONS.CHOOSE_FILE')"
              @click="openFilePicker('favicon')"
            />
            <span class="min-w-0 truncate text-sm text-n-slate-11">
              {{ favicon?.name || t('ACCOUNT_BRANDING.ACTIONS.NO_FILE_SELECTED') }}
            </span>
          </div>
          <div v-if="current.favicon_url && !removeFavicon" class="mt-2 flex items-center gap-3">
            <img
              :src="current.favicon_url"
              :alt="t('ACCOUNT_BRANDING.FAVICON.CURRENT_ALT')"
              class="size-10 object-contain"
            />
            <Button
              icon="i-lucide-trash-2"
              variant="ghost"
              color="ruby"
              size="sm"
              :label="t('ACCOUNT_BRANDING.ACTIONS.REMOVE')"
              @click="removeFavicon = true"
            />
          </div>
        </label>
      </section>
      <div class="flex justify-end">
        <Button
          type="submit"
          :is-loading="saving"
          icon="i-lucide-save"
          :label="t('ACCOUNT_BRANDING.ACTIONS.SAVE')"
        />
      </div>
    </form>
  </SettingsLayout>
</template>
